import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Crown, MessageCircle, Heart, Star, Zap, ArrowLeft, CreditCard, Tag, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { createStripeCheckout, createPayPalOrder, capturePayPalOrder, getPackages } from '../services/api';
import { validatePromo } from '../services/adminApi';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const PAYPAL_CLIENT_ID = 'test'; // Sandbox client ID

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = searchParams.get('return');
  const { user, isPremium } = useAuth();
  const [loading, setLoading] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dynamicPackages, setDynamicPackages] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);

  useEffect(() => {
    // Load packages from backend so admin changes reflect
    getPackages()
      .then(res => setDynamicPackages(res.data))
      .catch(() => setDynamicPackages(null));
  }, []);

  const subscriptionFeatures = [
    'Unlimited messaging',
    'See who likes you',
    'Boost your profile weekly',
    'Advanced filters',
    'No ads',
    'Priority support'
  ];

  // Build package list from backend if available, fall back to defaults for structure
  const packages = dynamicPackages
    ? Object.entries(dynamicPackages).map(([id, p]) => ({
        id,
        name: p.description || id,
        price: p.amount,
        type: p.type,
        credits: p.credits,
        bonus_credits: p.bonus_credits || 0,
        description: p.label || p.description,
        popular: p.label?.toLowerCase().includes('popular'),
        features: p.type === 'subscription' ? subscriptionFeatures : undefined
      }))
    : [
    {
      id: 'subscription_monthly',
      name: 'Premium Monthly',
      price: 9.99,
      period: '/month',
      type: 'subscription',
      popular: true,
      features: subscriptionFeatures
    },
    {
      id: 'credits_50',
      name: '50 Credits',
      price: 4.99,
      period: 'one-time',
      type: 'credits',
      credits: 50,
      description: '50 message credits'
    },
    {
      id: 'credits_100',
      name: '100 Credits',
      price: 7.99,
      period: 'one-time',
      type: 'credits',
      credits: 100,
      description: 'Best value for casual users'
    },
    {
      id: 'credits_250',
      name: '250 Credits',
      price: 14.99,
      period: 'one-time',
      type: 'credits',
      credits: 250,
      description: 'Best value pack'
    }
  ];

  const applyPromo = async (packageId) => {
    if (!promoCode.trim() || !packageId) return;
    setCheckingPromo(true);
    setPromoResult(null);
    try {
      const res = await validatePromo(promoCode.trim().toUpperCase(), packageId);
      setPromoResult(res.data);
    } catch (err) {
      setPromoResult({ valid: false, error: err.response?.data?.detail || 'Invalid code' });
    } finally {
      setCheckingPromo(false);
    }
  };

  const clearPromo = () => {
    setPromoCode('');
    setPromoResult(null);
  };

  const handleStripeCheckout = async (packageId) => {
    setLoading(packageId);
    try {
      // Persist return path so PaymentSuccessPage can pick it up after Stripe redirect
      if (returnPath) {
        try { localStorage.setItem('gv_return_path', returnPath); } catch { /* ignore */ }
      }
      const originUrl = window.location.origin;
      const code = promoResult?.valid ? promoResult.code : undefined;
      const response = await createStripeCheckout(packageId, originUrl, code);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout failed:', error);
      alert(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading('');
    }
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      const response = await capturePayPalOrder(selectedPackage.orderId, data.orderID);
      if (response.data.status === 'success') {
        navigate(returnPath || '/payment/success');
      }
    } catch (error) {
      console.error('PayPal capture failed:', error);
    }
  };

  const handlePayPalCreateOrder = async () => {
    if (!selectedPackage) return;
    try {
      const code = promoResult?.valid ? promoResult.code : undefined;
      const response = await createPayPalOrder(selectedPackage.id, code);
      setSelectedPackage(prev => ({ ...prev, orderId: response.data.order_id }));
      return response.data.order_id;
    } catch (error) {
      console.error('PayPal order creation failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8 px-4" data-testid="pricing-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              Upgrade to Premium
            </h1>
            <p className="text-gray-600">Unlock all features and find love faster</p>
          </div>
        </div>

        {/* Premium Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#009E60] to-[#007A4B] rounded-3xl p-8 mb-8 text-white relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FFCE00]/20 rounded-full -ml-24 -mb-24"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-8 h-8 text-[#FFCE00]" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Premium</span>
            </div>
            
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-5xl font-bold">$9.99</span>
              <span className="text-white/70">/month</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {subscriptionFeatures.map((feature, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#FFCE00]" />
                  <span className="text-white/90">{feature}</span>
                </div>
              ))}
            </div>

            {isPremium ? (
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <Check className="w-8 h-8 text-[#FFCE00] mx-auto mb-2" />
                <p className="font-semibold">You're already Premium!</p>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  onClick={() => handleStripeCheckout('subscription_monthly')}
                  disabled={loading === 'subscription_monthly'}
                  className="flex-1 h-12 bg-[#FFCE00] hover:bg-[#e6b800] text-[#0a0a0a] font-semibold rounded-full"
                  data-testid="subscribe-stripe-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  {loading === 'subscription_monthly' ? 'Loading...' : 'Pay with Card'}
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Credits Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Or buy message credits
          </h2>
          <p className="text-gray-600 mb-6">
            Pay per message without a subscription. 1 credit = 1 message.
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            {packages.filter(p => p.type === 'credits').map((pkg) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-2xl p-6 shadow-sm border-2 transition-all cursor-pointer ${
                  selectedPackage?.id === pkg.id ? 'border-[#009E60]' : 'border-transparent hover:border-gray-200'
                }`}
                onClick={() => {
                  setSelectedPackage(pkg);
                  setPaymentMethod(null);
                }}
                data-testid={`package-${pkg.id}`}
              >
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#FFCE00]/20 flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-6 h-6 text-[#FFCE00]" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{pkg.name}</h3>
                  <div className="text-2xl font-bold text-gray-900 my-2">${pkg.price}</div>
                  {pkg.bonus_credits > 0 && (
                    <div className="text-xs bg-[#FFCE00] text-[#0a0a0a] rounded-full px-2 py-0.5 inline-block font-semibold mb-1">
                      +{pkg.bonus_credits} bonus
                    </div>
                  )}
                  <p className="text-sm text-gray-500">{pkg.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Payment Section */}
        {selectedPackage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-sm mb-8"
          >
            <h3 className="font-semibold text-gray-900 mb-4">
              Pay for {selectedPackage.name}
            </h3>

            {/* Promo Code Input */}
            <div className="mb-4">
              {promoResult?.valid ? (
                <div className="flex items-center justify-between p-3 bg-[#009E60]/10 border border-[#009E60]/30 rounded-xl" data-testid="promo-applied">
                  <div>
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#009E60]" />
                      <span className="font-semibold text-[#009E60] font-mono">{promoResult.code}</span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      Saving <span className="font-semibold">${promoResult.savings.toFixed(2)}</span> · You pay <span className="font-bold">${promoResult.final_amount.toFixed(2)}</span> <span className="text-gray-400 line-through text-xs">${promoResult.base_amount.toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={clearPromo} className="p-2 hover:bg-white rounded-full">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Promo code"
                        className="pl-9 h-11 rounded-xl font-mono"
                        data-testid="promo-code-field"
                      />
                    </div>
                    <Button
                      onClick={() => applyPromo(selectedPackage.id)}
                      disabled={checkingPromo || !promoCode.trim()}
                      variant="outline"
                      className="rounded-xl h-11"
                      data-testid="apply-promo-btn"
                    >
                      {checkingPromo ? 'Checking...' : 'Apply'}
                    </Button>
                  </div>
                  {promoResult?.error && (
                    <p className="text-sm text-red-600">{promoResult.error}</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => handleStripeCheckout(selectedPackage.id)}
                disabled={loading === selectedPackage.id}
                className="w-full h-12 bg-[#009E60] hover:bg-[#007A4B] rounded-full"
                data-testid="pay-stripe-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {loading === selectedPackage.id ? 'Processing...' : `Pay $${(promoResult?.valid ? promoResult.final_amount : selectedPackage.price).toFixed(2)} with Card`}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD' }}>
                <PayPalButtons
                  style={{ layout: 'horizontal', height: 48 }}
                  createOrder={handlePayPalCreateOrder}
                  onApprove={handlePayPalApprove}
                  onError={(err) => console.warn('PayPal unavailable:', err)}
                  disabled={!selectedPackage}
                  forceReRender={[promoResult?.valid]}
                />
              </PayPalScriptProvider>
            </div>
          </motion.div>
        )}

        {/* Benefits */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <div className="w-12 h-12 rounded-full bg-[#E2231A]/10 flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-[#E2231A]" />
            </div>
            <h4 className="font-semibold text-gray-900">More Matches</h4>
            <p className="text-sm text-gray-500 mt-1">Premium users get 3x more matches</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <div className="w-12 h-12 rounded-full bg-[#FFCE00]/20 flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-[#FFCE00]" />
            </div>
            <h4 className="font-semibold text-gray-900">Stand Out</h4>
            <p className="text-sm text-gray-500 mt-1">Your profile gets priority placement</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <div className="w-12 h-12 rounded-full bg-[#009E60]/10 flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-[#009E60]" />
            </div>
            <h4 className="font-semibold text-gray-900">Instant Results</h4>
            <p className="text-sm text-gray-500 mt-1">Start messaging immediately</p>
          </div>
        </div>

        {/* Money-back guarantee */}
        <div className="text-center text-gray-500 text-sm">
          <p>Secure payment processing. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
